/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarDays, IndianRupee, Stethoscope, Users } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../components/ui/stat-card.js';
import { type DashboardStats } from '../../lib/api/dashboard.js';
import { formatInr } from '../../lib/utils/money.js';

/**
 * The four headline metric tiles (Standalone-2 design): Active Patients,
 * Therapists (engagement split), Today's Sessions (status breakdown) and
 * Revenue (with overdue). Trends/sublines are server-computed.
 */
export function StatsRow({ stats }: { readonly stats: DashboardStats }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={Users}
        accent="bg-[var(--accent)]"
        label={t('admin.dashboard.activeClients')}
        value={String(stats.activeClients)}
        trend={stats.activeClientsTrendPct ?? undefined}
      />
      <StatCard
        icon={Stethoscope}
        accent="bg-[var(--accent)]"
        label={t('admin.dashboard.therapists')}
        value={String(stats.therapistsTotal)}
        hint={t('admin.dashboard.therapistSplit', {
          onRoll: stats.therapistsOnRoll,
          consultant: stats.therapistsConsultant,
        })}
      />
      <StatCard
        icon={CalendarDays}
        accent="bg-[var(--accent)]"
        label={t('admin.dashboard.todaySessions')}
        value={String(stats.todaySessions)}
        hint={t('admin.dashboard.todayBreakdown', {
          completed: stats.todayCompleted,
          inProgress: stats.todayInProgress,
        })}
      />
      <StatCard
        icon={IndianRupee}
        accent="bg-[var(--accent)]"
        label={t('admin.dashboard.totalRevenue')}
        value={formatInr(stats.revenuePaise)}
        trend={stats.revenueTrendPct ?? undefined}
        hint={
          stats.overduePaise > 0
            ? t('admin.dashboard.overdue', { amount: formatInr(stats.overduePaise) })
            : undefined
        }
      />
    </div>
  );
}
